import { type FC, useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { BasicModal } from '../../Modals/Basic';

interface ImageLightboxProps {
  show: boolean;
  imageUrls: string[];
  initialIndex: number;
  onClose: () => void;
}

export const ImageLightbox: FC<ImageLightboxProps> = ({
  show,
  imageUrls,
  initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (show) setCurrentIndex(initialIndex);
  }, [show, initialIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(imageUrls.length - 1, prev + 1));
  }, [imageUrls.length]);

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, goToPrev, goToNext]);

  if (!imageUrls.length) return null;

  return (
    <BasicModal show={show} handleClose={onClose} size="xl">
      <div className="image-lightbox-body">
        {currentIndex > 0 && (
          <button
            type="button"
            className="image-lightbox-nav image-lightbox-nav--prev"
            onClick={goToPrev}
          >
            <ChevronLeft size={20} />
          </button>
        )}

        <img
          alt={`Attachment ${currentIndex + 1} of ${imageUrls.length}`}
          src={imageUrls[currentIndex]}
        />

        {currentIndex < imageUrls.length - 1 && (
          <button
            type="button"
            className="image-lightbox-nav image-lightbox-nav--next"
            onClick={goToNext}
          >
            <ChevronRight size={20} />
          </button>
        )}

        {imageUrls.length > 1 && (
          <div className="image-lightbox-counter">
            {currentIndex + 1} / {imageUrls.length}
          </div>
        )}
      </div>
    </BasicModal>
  );
};
